/**
 * Watches By AB — Track Your Order (front end)
 *
 * Talks to /apps/track (Shopify App Proxy -> Cloudflare Worker), which returns
 * already-cleaned tracking data. This file only decides what to SAY:
 * headline, delivery estimate, progress bar, the "What's happening now" card,
 * the timeline, and when to switch to an apologetic tone.
 *
 * No libraries, no build step. Works with several copies of the section on a page.
 */
(function () {
  'use strict';

  const DAY = 86400000;
  const STAGE_KEYS = ['order_confirmed', 'being_prepared', 'label_created', 'shipped', 'in_transit', 'in_country', 'out_for_delivery', 'delivered'];
  const VISIBLE_EVENTS = 4;

  // ---------- small helpers ----------------------------------------------

  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const fmtDate = (d, withYear) =>
    new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}) }).format(d);
  const fmtWeekday = (d) => new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' }).format(d);
  const fmtTime = (d) => new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(d);
  const daysSince = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / DAY : 0);

  function addBusinessDays(date, n) {
    const d = new Date(date);
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      const wd = d.getDay();
      if (wd !== 0 && wd !== 6) added++;
    }
    return d;
  }

  /** Fill {placeholders} in editable copy. */
  function fill(template, vars) {
    return String(template || '').replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null && vars[k] !== '' ? vars[k] : m));
  }

  /** Test-mode mocks were generated at build time — shift their dates to "now". */
  function shiftDates(obj, deltaMs) {
    if (Array.isArray(obj)) return obj.map((v) => shiftDates(v, deltaMs));
    if (obj && typeof obj === 'object') {
      const out = {};
      for (const k in obj) out[k] = shiftDates(obj[k], deltaMs);
      return out;
    }
    if (typeof obj === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(obj)) return new Date(new Date(obj).getTime() + deltaMs).toISOString();
    return obj;
  }

  // ---------- one section instance ---------------------------------------

  class OrderTracker {
    constructor(root) {
      this.root = root;
      this.cfg = JSON.parse(root.querySelector('[data-config]').textContent);
      this.endpoint = root.dataset.endpoint || '/apps/track';
      this.testMode = root.dataset.testMode === 'true';
      this.designMode = !!(window.Shopify && window.Shopify.designMode);
      this.mockScenario = 'in_country';
      this.mode = 'order';

      this.form = root.querySelector('[data-lookup-form]');
      this.errorEl = root.querySelector('[data-error]');
      this.resultEl = root.querySelector('[data-result]');
      this.submitBtn = root.querySelector('[data-submit]');

      this.bindForm();
      this.bindContact();
      this.setupDemo();
      this.prefillFromUrl();
      if (root.dataset.loggedIn === 'true') this.loadMyOrders();
    }

    // ---- data access -----------------------------------------------------

    get useMocks() {
      return (this.testMode || this.designMode) && window.WBAB_TRACK_MOCKS;
    }

    async api(body) {
      if (this.useMocks) return this.mockApi(body);
      const res = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      let data = null;
      try {
        data = await res.json();
      } catch (e) {
        /* proxy not set up yet, or HTML error page */
      }
      if (!data) return { ok: false, error: 'server' };
      return data;
    }

    async mockApi(body) {
      const m = window.WBAB_TRACK_MOCKS;
      const delta = Date.now() - new Date(m.built_at).getTime();
      await new Promise((r) => setTimeout(r, 650)); // let the skeleton show
      if (body.mine) return { ok: true, orders: shiftDates(m.customer_orders, delta) };
      let key = this.mockScenario;
      if (body.order_id && String(body.order_id).startsWith('demo:')) key = body.order_id.slice(5);
      if (key === 'error_not_found' || /^0+$/.test(String(body.order || '').replace(/\D/g, ''))) return { ok: false, error: 'not_found' };
      const sc = m.scenarios.find((s) => s.key === key) || m.scenarios[0];
      return shiftDates(sc.response, delta);
    }

    // ---- form --------------------------------------------------------------

    bindForm() {
      this.root.querySelectorAll('.wbab-tab').forEach((tab) => {
        tab.addEventListener('click', () => this.setMode(tab.dataset.mode));
      });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const fd = new FormData(this.form);
        if (this.mode === 'tracking') {
          const tracking = String(fd.get('tracking') || '').trim();
          if (!tracking) return this.showError('invalid');
          this.lookup({ tracking });
        } else {
          const order = String(fd.get('order') || '').trim();
          const email = String(fd.get('email') || '').trim();
          if (!order || !email) return this.showError('invalid');
          this.lookup({ order, email });
        }
      });
    }

    setMode(mode) {
      this.mode = mode;
      this.root.querySelectorAll('.wbab-tab').forEach((t) => {
        const on = t.dataset.mode === mode;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.root.querySelectorAll('[data-fields]').forEach((f) => {
        const on = f.dataset.fields === mode;
        f.hidden = !on;
        f.querySelectorAll('input').forEach((i) => (i.disabled = !on));
      });
    }

    /** /pages/track?order=1562 (e.g. from a shipping email) pre-fills the number. */
    prefillFromUrl() {
      const p = new URLSearchParams(location.search);
      if (p.get('order')) this.form.elements.order.value = p.get('order');
      if (p.get('tracking')) {
        this.setMode('tracking');
        this.form.elements.tracking.value = p.get('tracking');
        this.lookup({ tracking: p.get('tracking') });
      }
    }

    async lookup(body) {
      clearTimeout(this.recheckTimer);
      this.lastQuery = body;
      this.hideError();
      this.showSkeleton();
      this.submitBtn.disabled = true;
      try {
        const data = await this.api(body);
        if (!data.ok) {
          this.resultEl.hidden = true;
          this.showError(data.error || 'server');
          return;
        }
        this.data = data;
        if (!body.__recheck) this.shipmentIdx = 0;
        this.render();
        this.scheduleRecheck(body);
      } catch (e) {
        this.resultEl.hidden = true;
        this.showError('server');
      } finally {
        this.submitBtn.disabled = false;
      }
    }

    /**
     * The very first lookup of a shipment can arrive before the carrier
     * history has synced. Quietly look again (every 20 s, up to 6 times) so
     * the real status appears without the customer refreshing.
     */
    scheduleRecheck(body) {
      clearTimeout(this.recheckTimer);
      const pending = this.data && this.data.shipments.some((s) => s.tracking_pending && s.number);
      if (!pending) {
        this.recheckCount = 0;
        return;
      }
      if (!body.__recheck) this.recheckCount = 0;
      if (this.recheckCount >= 6 || this.useMocks) return;
      this.recheckCount += 1;
      this.recheckTimer = setTimeout(async () => {
        try {
          const query = Object.assign({}, body);
          delete query.__recheck;
          const data = await this.api(query);
          if (data && data.ok) {
            this.data = data;
            this.render();
          }
        } catch (e) {
          /* keep showing what we have */
        }
        this.scheduleRecheck(Object.assign({}, body, { __recheck: true }));
      }, 20000);
    }

    showError(code) {
      const t = this.cfg.text;
      const msg = { not_found: t.error_not_found, tracking_unknown: t.error_tracking_unknown, invalid: t.error_invalid, rate_limited: t.error_rate_limited }[code] || t.error_server;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }

    hideError() {
      this.errorEl.hidden = true;
    }

    showSkeleton() {
      this.resultEl.hidden = false;
      this.resultEl.setAttribute('aria-busy', 'true');
      this.resultEl.innerHTML = `
        <div class="wbab-skel" aria-label="${esc(this.cfg.text.loading)}">
          <span class="wbab-skel__line wbab-skel__line--sm"></span>
          <span class="wbab-skel__line wbab-skel__line--lg"></span>
          <span class="wbab-skel__line wbab-skel__line--md"></span>
          <span class="wbab-skel__bar"></span>
          <span class="wbab-skel__card"></span>
          <span class="wbab-skel__line"></span><span class="wbab-skel__line"></span><span class="wbab-skel__line wbab-skel__line--md"></span>
        </div>`;
    }

    // ---- logged-in customers ---------------------------------------------

    async loadMyOrders() {
      const wrap = this.root.querySelector('[data-my-orders]');
      const list = this.root.querySelector('[data-my-orders-list]');
      let data;
      try {
        data = await this.api({ mine: true });
      } catch (e) {
        return;
      }
      if (!data.ok || !data.orders || !data.orders.length) return;
      list.innerHTML = data.orders
        .map(
          (o, i) => `
          <button type="button" class="wbab-order-chip${i === 0 ? ' is-active' : ''}" data-order-id="${esc(o.id)}">
            <span class="wbab-order-chip__name">${esc(o.name)}</span>
            <span class="wbab-order-chip__meta">${esc(fmtDate(new Date(o.created_at)))}${o.items && o.items[0] ? ' · ' + esc(o.items[0].title) : ''}</span>
          </button>`,
        )
        .join('');
      wrap.hidden = false;
      list.querySelectorAll('[data-order-id]').forEach((btn) =>
        btn.addEventListener('click', () => {
          list.querySelectorAll('.wbab-order-chip').forEach((b) => b.classList.toggle('is-active', b === btn));
          this.lookup({ order_id: btn.dataset.orderId });
        }),
      );
      // Auto-show the most recent order.
      this.lookup({ order_id: data.orders[0].id });
    }

    // ---- deciding what to say -------------------------------------------

    /**
     * Delivery window: carrier's estimate once in-country (if enabled), else
     * order date + business days. Tracking-number lookups have no order, so
     * they count from the label date (made minutes after the order).
     */
    estimate(order, sh) {
      const e = this.cfg.estimate;
      if (sh.stage === 'delivered') return null;
      if (e.use_carrier_eta && sh.carrier_eta && sh.in_destination_country) {
        return { from: new Date(sh.carrier_eta.from), to: new Date(sh.carrier_eta.to) };
      }
      const start = order ? order.created_at : sh.label_at;
      if (!start) return null;
      const dest = order ? order.destination : sh.destination;
      const domestic = (dest || '').toUpperCase() === (e.domestic_country || 'US');
      let min = domestic ? e.domestic_min : e.intl_min;
      let max = domestic ? e.domestic_max : e.intl_max;
      // Test mode only: sample window so the estimate + "delayed" state can be previewed before you fill in the real numbers.
      if ((min == null || max == null) && this.useMocks) {
        min = 8;
        max = 15;
      }
      if (min == null || max == null) return null;
      const placed = new Date(start);
      return { from: addBusinessDays(placed, min), to: addBusinessDays(placed, max) };
    }

    /** Past the end of the estimated window (and not finished)? -> apologetic tone. */
    isDelayed(order, sh, est) {
      if (!est || (order && order.cancelled)) return false;
      if (sh.stage === 'delivered' || sh.exception === 'returned') return false;
      const end = new Date(est.to);
      end.setHours(23, 59, 59, 999);
      return Date.now() > end.getTime();
    }

    /** Which "What's happening now" message fits. */
    cardKey(order, sh, delayed) {
      const trig = this.cfg.triggers;
      if (sh.exception) return sh.exception; // cancelled, returned, held, customs_info, attempted, pickup
      if (sh.stage === 'delivered') return 'delivered';
      if (delayed) return 'delayed';
      if (sh.stage === 'out_for_delivery') return 'out_for_delivery';
      if (sh.stage === 'being_prepared') return 'being_prepared';
      if (sh.customs) return 'customs';
      if (sh.tracking_pending) return 'tracking_pending';
      if (sh.stage === 'label_created') {
        return daysSince(sh.label_at) >= Number(trig.label_no_movement_days) ? 'label_no_movement' : 'label_created';
      }
      if (sh.stage === 'in_country') return 'in_country';
      // shipped / in transit
      return daysSince(sh.last_event_at) >= Number(trig.quiet_days) ? 'quiet' : 'in_transit';
    }

    headline(sh, delayed) {
      const h = this.cfg.headlines;
      if (sh.exception && h[sh.exception]) return h[sh.exception];
      if (sh.tracking_pending && h.tracking_pending) return h.tracking_pending;
      if (delayed && sh.stage !== 'delivered') return h.delayed;
      return h[sh.stage] || h.in_transit;
    }

    vars(order, sh) {
      const carrier = (sh.local && sh.local.carrier) || (sh.carriers[0] && sh.carriers[0].carrier) || 'the carrier';
      return Object.assign({}, this.cfg.timeframes, {
        country: sh.destination_name || (order && order.destination_name) || 'your country',
        carrier,
        order: order ? order.name : '',
      });
    }

    // ---- rendering ---------------------------------------------------------

    render() {
      const { order, shipments } = this.data;
      const sh = shipments[this.shipmentIdx] || shipments[0];
      const t = this.cfg.text;
      const vars = this.vars(order, sh);
      const est = this.estimate(order, sh);
      const delayed = this.isDelayed(order, sh, est);
      const cardKey = this.cardKey(order, sh, delayed);
      const urgent = delayed || ['held', 'customs_info', 'returned', 'cancelled'].includes(cardKey);
      this.currentStatus = cardKey;

      const parts = [];

      // Header: order number + headline + estimate
      const meta = order
        ? `${esc(t.order_prefix)} ${esc(order.name)} · ${esc(t.placed_on)} ${esc(fmtDate(new Date(order.created_at)))}`
        : sh.number ? esc(sh.number) : '';
      let estLine = '';
      if (sh.stage === 'delivered' && sh.delivered_at) {
        estLine = `${esc(this.cfg.estimate.delivered_label)} ${esc(fmtWeekday(new Date(sh.delivered_at)))}`;
      } else if (est && !(order && order.cancelled) && sh.exception !== 'returned') {
        const same = est.from.toDateString() === est.to.toDateString();
        const label = delayed ? this.cfg.estimate.late_label : this.cfg.estimate.label;
        estLine = `${esc(label)}: <strong>${esc(fmtWeekday(est.from))}${same ? '' : ' – ' + esc(fmtWeekday(est.to))}</strong>`;
      }

      parts.push(`
        <div class="wbab-status${urgent ? ' is-urgent' : ''}${sh.stage === 'delivered' ? ' is-delivered' : ''}">
          ${meta ? `<p class="wbab-label">${meta}</p>` : ''}
          <h2 class="wbab-status__headline" tabindex="-1">${esc(fill(this.headline(sh, delayed), vars))}</h2>
          ${estLine ? `<p class="wbab-status__eta">${estLine}</p>` : ''}
          ${this.renderProgress(sh)}
        </div>`);

      // Several packages on one order
      if (shipments.length > 1) {
        parts.push(`<div class="wbab-packages" role="tablist">${shipments
          .map((s, i) => `<button type="button" role="tab" aria-selected="${i === this.shipmentIdx}" class="wbab-package${i === this.shipmentIdx ? ' is-active' : ''}" data-pkg="${i}">${esc(t.package)} ${i + 1}</button>`)
          .join('')}</div>`);
      }

      // "What's happening now" card
      const cards = this.cfg.cards;
      const cardText = fill(cards[cardKey] || '', vars);
      const extra = order && order.partially_fulfilled && cardKey !== 'being_prepared' ? `<p>${esc(fill(cards.partially_fulfilled, vars))}</p>` : '';
      const showContact = urgent || ['attempted', 'pickup'].includes(cardKey);
      parts.push(`
        <div class="wbab-card${urgent ? ' wbab-card--urgent' : ''}">
          <p class="wbab-card__title"><span class="wbab-card__dot" aria-hidden="true"></span>${esc(cards.title)}</p>
          <p>${esc(cardText)}</p>
          ${extra}
          ${showContact ? `<button type="button" class="wbab-btn wbab-btn--primary wbab-card__cta" data-open-contact>${esc(t.contact_cta)}</button>` : ''}
        </div>`);

      // Local (last-mile) tracking number, once the handoff happened
      if (sh.local && sh.local.number) {
        parts.push(`
          <p class="wbab-local">${esc(t.local_tracking)} (${esc(sh.local.carrier)}):
            ${sh.local.url ? `<a href="${esc(sh.local.url)}" target="_blank" rel="noopener">${esc(sh.local.number)}</a>` : `<span>${esc(sh.local.number)}</span>`}
          </p>`);
      }

      // Timeline (newest first, collapsed after 4)
      const events = sh.events.slice();
      if (order) events.push({ time: order.created_at, title: t.order_confirmed_event, location: null });
      if (events.length) {
        const lastUpdate = sh.last_event_at ? `<span class="wbab-muted">${esc(t.last_update)} ${esc(fmtDate(new Date(sh.last_event_at)))}</span>` : '';
        parts.push(`
          <div class="wbab-timeline">
            <div class="wbab-timeline__head"><h3 class="wbab-h3">${esc(t.timeline_heading)}</h3>${lastUpdate}</div>
            <ol class="wbab-timeline__list">
              ${events
                .map((e, i) => {
                  const d = new Date(e.time);
                  return `<li class="wbab-event${i === 0 ? ' is-latest' : ''}"${i >= VISIBLE_EVENTS ? ' data-extra hidden' : ''}>
                    <span class="wbab-event__dot" aria-hidden="true"></span>
                    <div class="wbab-event__body">
                      <p class="wbab-event__title">${esc(e.title)}</p>
                      <p class="wbab-event__meta">${esc(fmtDate(d))} · ${esc(fmtTime(d))}${e.location ? ' · ' + esc(e.location) : ''}</p>
                    </div>
                  </li>`;
                })
                .join('')}
            </ol>
            ${events.length > VISIBLE_EVENTS ? `<button type="button" class="wbab-link" data-toggle-events aria-expanded="false">${esc(t.show_all)} (${events.length})</button>` : ''}
          </div>`);
      }

      // Real carrier links (may show the full, unfiltered carrier history)
      if (sh.carriers && sh.carriers.length) {
        parts.push(`
          <details class="wbab-carriers">
            <summary>${esc(t.carrier_details)}</summary>
            <ul>
              ${sh.carriers
                .map((c) => `<li><span>${esc(c.carrier)} · ${esc(c.number)}</span>${c.url ? ` <a href="${esc(c.url)}" target="_blank" rel="noopener">${esc(t.carrier_link)} ↗</a>` : ''}</li>`)
                .join('')}
            </ul>
          </details>`);
      }

      this.resultEl.innerHTML = parts.join('');
      this.resultEl.removeAttribute('aria-busy');
      this.resultEl.hidden = false;
      this.bindResult(events.length);
      this.prefillContact(order, cardKey);

      const h = this.resultEl.querySelector('.wbab-status__headline');
      const silent = this.recheckCount > 0;
      if (h && !this.designMode && !silent) h.focus({ preventScroll: true });
      if (window.matchMedia('(max-width: 749px)').matches && !this.designMode && !silent) {
        this.resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    renderProgress(sh) {
      const labels = this.cfg.stage_labels;
      const current = sh.stage_index || 1;
      const steps = labels
        .map((label, i) => {
          const n = i + 1;
          const state = n < current ? 'done' : n === current ? 'current' : 'todo';
          return `<li class="wbab-progress__step is-${state}${n === current && sh.exception ? ' is-alert' : ''}"${n === current ? ' aria-current="step"' : ''}>
              <span class="wbab-progress__seg" aria-hidden="true"></span>
              <span class="wbab-progress__label">${esc(label)}</span>
            </li>`;
        })
        .join('');
      return `
        <div class="wbab-progress">
          <p class="wbab-progress__mobile">${esc(labels[current - 1] || '')}<span> · ${current}/${labels.length}</span></p>
          <ol class="wbab-progress__steps" style="--wbab-steps:${labels.length}">${steps}</ol>
        </div>`;
    }

    bindResult(eventCount) {
      const toggle = this.resultEl.querySelector('[data-toggle-events]');
      if (toggle) {
        toggle.addEventListener('click', () => {
          const open = toggle.getAttribute('aria-expanded') === 'true';
          this.resultEl.querySelectorAll('[data-extra]').forEach((li) => (li.hidden = open));
          toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
          toggle.textContent = open ? `${this.cfg.text.show_all} (${eventCount})` : this.cfg.text.show_less;
        });
      }
      this.resultEl.querySelectorAll('[data-pkg]').forEach((b) =>
        b.addEventListener('click', () => {
          this.shipmentIdx = Number(b.dataset.pkg);
          this.render();
        }),
      );
      const cta = this.resultEl.querySelector('[data-open-contact]');
      if (cta) cta.addEventListener('click', () => this.openContact(true));
    }

    // ---- contact form ------------------------------------------------------

    bindContact() {
      const toggle = this.root.querySelector('[data-contact-toggle]');
      if (toggle) toggle.addEventListener('click', () => this.openContact(false));
    }

    openContact(scroll) {
      const fields = this.root.querySelector('[data-contact-fields]');
      const toggle = this.root.querySelector('[data-contact-toggle]');
      if (fields) fields.hidden = false;
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'true');
        toggle.hidden = true;
      }
      const help = this.root.querySelector('[data-help]');
      if (scroll && help) help.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const body = this.root.querySelector('[data-contact-body]');
      if (body) setTimeout(() => body.focus({ preventScroll: true }), scroll ? 400 : 0);
    }

    /** Pre-fill the contact form with the order number, email and current status. */
    prefillContact(order, statusKey) {
      const orderInput = this.root.querySelector('[data-contact-order]');
      const emailInput = this.root.querySelector('[data-contact-email]');
      const statusInput = this.root.querySelector('[data-contact-status]');
      if (orderInput && order) orderInput.value = order.name;
      else if (orderInput && this.lastQuery && this.lastQuery.tracking) orderInput.value = `Tracking ${this.lastQuery.tracking}`;
      if (emailInput && this.lastQuery && this.lastQuery.email && !emailInput.value) emailInput.value = this.lastQuery.email;
      if (statusInput) statusInput.value = statusKey;
    }

    // ---- test mode -------------------------------------------------------

    setupDemo() {
      const bar = this.root.querySelector('[data-demo-bar]');
      if (!bar || !window.WBAB_TRACK_MOCKS) return;
      const wrap = bar.querySelector('[data-demo-buttons]');
      const items = window.WBAB_TRACK_MOCKS.scenarios.map((s) => ({ key: s.key, label: s.label }));
      items.push({ key: 'error_not_found', label: 'Error: not found' });
      wrap.innerHTML = items
        .map((s) => `<button type="button" class="wbab-demo__btn${s.key === this.mockScenario ? ' is-active' : ''}" data-demo="${esc(s.key)}">${esc(s.label)}</button>`)
        .join('');
      wrap.querySelectorAll('[data-demo]').forEach((b) =>
        b.addEventListener('click', () => {
          this.mockScenario = b.dataset.demo;
          wrap.querySelectorAll('[data-demo]').forEach((x) => x.classList.toggle('is-active', x === b));
          this.lookup({ order: '#1562', email: 'test@example.com' });
        }),
      );
      bar.hidden = false;
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-wbab-track]').forEach((el) => {
      if (el.__wbabTracker) return;
      // Mocks load with `defer` too; wait for them if this is a test-mode page.
      el.__wbabTracker = new OrderTracker(el);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Theme editor: re-init when the section is added or its settings change.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
